/**
 * Engraving Fit Engine
 * Core logic for calculating whether engraving text fits on a ring.
 * Used by: Product page fit indicator, Ring Builder fit indicator.
 *
 * Dependencies:
 * - Requires engraving fonts loaded via @font-face (from engraving.css or component-engraving-form.css)
 * - Optionally uses TengwarEngine global (from elvish-engraving-calculator-v7.js) for Elvish transliteration
 */

window.EngravingFitEngine = (function () {
  'use strict';

  // ── Ring geometry ──────────────────────────────────────────────
  const BAND_CONFIGS = [
    { nominal: 3, name: 'Petite 3mm', height: 2.4, marginMm: 0.37 },
    { nominal: 4, name: 'Petite 4mm', height: 3.0, marginMm: 0.37 },
    { nominal: 5.5, name: 'Little One', height: 4.35, marginMm: 0.61 },
    { nominal: 7.2, name: 'The Movie Ring', height: 5.0, marginMm: 0.98 },
  ];

  const DEFAULT_GAP_MM = 3; // resizing clearance
  const DEFAULT_HALLMARK_MM = 9; // inside hallmark reservation (5mm for bands under 4mm)

  function getThickness(bandWidth) {
    return 1.8 + ((bandWidth - 3) / 4.2) * 1.1;
  }

  function getInsideDiameter(ringSize) {
    return ringSize * 0.8128 + 11.63;
  }

  function getCircumference(ringSize, bandWidth, position) {
    const id = getInsideDiameter(ringSize);
    const th = getThickness(bandWidth);
    return position === 'inside' ? Math.PI * id : Math.PI * (id + 2 * th);
  }

  function getHallmarkMm(bandWidth) {
    return bandWidth < 4 ? 5 : DEFAULT_HALLMARK_MM;
  }

  // ── Font configs (metadata only, no embedded data) ─────────────
  // Font families must match @font-face declarations in the page CSS.
  // The "Elvish" family name matches the engraving.css @font-face.
  const FONT_CONFIGS = [
    { key: 'tengwar', name: 'Tengwar Elvish', family: 'Elvish', scale: 0.4214, tengwar: true, style: 'italic', adj: 1.0 },
    { key: 'kereru', name: 'Kereru', family: 'Kereru', scale: 0.7116, tengwar: false, style: 'normal', adj: 0.87 },
    { key: 'kereruItalic', name: 'Kereru Italic', family: 'Kereru Italic', scale: 0.7116, tengwar: false, style: 'italic', adj: 0.87 },
    { key: 'bookman', name: 'Bookman Old Style', family: 'Bookman Old Style', scale: 0.8889, tengwar: false, style: 'normal', adj: 0.87 },
    { key: 'colonna', name: 'Colonna MT', family: 'Colonna MT', scale: 0.8889, tengwar: false, style: 'normal', adj: 0.92 },
    { key: 'edwardian', name: 'Edwardian Script', family: 'Edwardian Script', scale: 0.811, tengwar: false, style: 'normal', adj: 0.92 },
    { key: 'lucidaHW', name: 'Lucida Handwriting', family: 'Lucida Handwriting', scale: 0.7787, tengwar: false, style: 'italic', adj: 0.92 },
    { key: 'lucidaCal', name: 'Lucida Calligraphy', family: 'Lucida Calligraphy', scale: 0.7348, tengwar: false, style: 'normal', adj: 0.92 },
    { key: 'chancery', name: 'Apple Chancery', family: 'Apple Chancery', scale: 0.6242, tengwar: false, style: 'normal', adj: 0.92 },
    { key: 'oldEnglish', name: 'Old English', family: 'Old English', scale: 0.8475, tengwar: false, style: 'normal', adj: 0.92 },
    { key: 'scriptBold', name: 'Script Bold', family: 'Script Bold', scale: 0.8305, tengwar: false, style: 'normal', adj: 0.87 },
    { key: 'typewriter', name: 'Typewriter', family: 'Typewriter', scale: 0.9497, tengwar: false, style: 'normal', adj: 0.92 },
  ];

  // ── Canvas-based text measurement ──────────────────────────────
  let _canvas = null;
  let _ctx = null;
  let _effectiveScales = {};
  let _ready = false;

  function initMeasurement() {
    if (_ready) return Promise.resolve();

    return new Promise(function (resolve) {
      _canvas = document.createElement('canvas');
      _ctx = _canvas.getContext('2d');

      document.fonts.ready.then(function () {
        var loadPromises = FONT_CONFIGS.map(function (f) {
          return document.fonts.load('48px ' + f.family).catch(function () {});
        });

        Promise.all(loadPromises).then(function () {
          var refSize = 100;
          FONT_CONFIGS.forEach(function (fc) {
            if (fc.tengwar) {
              _effectiveScales[fc.key] = fc.scale;
              return;
            }
            _ctx.font = fc.style + ' ' + refSize + 'px ' + fc.family + ', serif';
            var m = _ctx.measureText('Hjgpqy');
            var asc = m.actualBoundingBoxAscent || refSize * 0.8;
            var desc = m.actualBoundingBoxDescent || refSize * 0.2;
            var actualH = asc + desc;
            _effectiveScales[fc.key] = actualH > 0 ? (refSize / actualH) * (fc.adj || 0.92) : fc.scale;
          });
          _ready = true;
          resolve();
        }).catch(function () {
          _ready = true;
          resolve();
        });
      });
    });
  }

  function measureText(text, heightMm, scalePxPerMm, vScale, fontFamily, fontStyle, fontScale, fontKey) {
    if (!_ctx || !text) return { widthMm: 0, widthPx: 0 };
    var scale = _effectiveScales[fontKey] || fontScale;
    var fontSize = heightMm * scalePxPerMm * scale * (vScale || 1.0);
    _ctx.font = fontStyle + ' ' + fontSize + 'px ' + fontFamily + ', serif';
    var m = _ctx.measureText(text);
    return { widthPx: m.width, widthMm: m.width / scalePxPerMm };
  }

  // ── Transliteration ────────────────────────────────────────────
  // Prefers window.transcribe (vendored canonical engraving.min.js).
  // Falls back to a TengwarEngine global if exposed by another page.
  // Final fallback returns raw text (measurement will be approximate).
  function transliterate(text) {
    if (!text || !text.trim()) return { text: '', hasErrors: false };

    var processed = text
          .replace(/[\u201C\u201D]/g, "'")
          .replace(/"/g, "'")
          .replace(/[\u2018\u2019]/g, "'")
          .replace(/\n/g, ' ');
    // Preferred: window.transcribe from vendored canonical engraving.min.js
    if (typeof window !== 'undefined' && typeof window.transcribe === 'function') {
      try {
        var out = window.transcribe(processed);
        var raw = String(out || '').replace(/<[^>]*>/g, '');
        return { text: raw, hasErrors: false };
      } catch (e) {
        // fall through to alternative
      }
    }

    // Alternative: TengwarEngine namespace
    if (typeof TengwarEngine !== 'undefined' && TengwarEngine && TengwarEngine.tengwar) {
      try {
        var html = TengwarEngine.tengwar.transcribe(processed, {
          font: TengwarEngine.fonts.annatar,
          noAchLaut: true,
          medialOre: true,
          language: 'english'
        });
        var raw2 = html.replace(/<[^>]*>/g, '');
        return { text: raw2, hasErrors: html.includes('class="error"') };
      } catch (e) {
        return { text: text, hasErrors: true };
      }
    }

    // Final fallback: raw text (Elvish font will visually transform it).
    return { text: text, hasErrors: false };
  }

  // ── Fit calculation ────────────────────────────────────────────
  /**
   * Calculate whether engraving text fits on a ring.
   *
   * @param {Object} params
   * @param {string} params.text — English text to engrave
   * @param {string} params.font — Font name (e.g. 'Elvish', 'Kereru', 'Lucida Handwriting')
   * @param {number} params.ringSize — US ring size (3–13)
   * @param {number} params.bandWidth — Band width in mm (3, 4, 5.5, or 7.2)
   * @param {string} params.position — 'inside' or 'outside'
   * @param {number} [params.vScale=1.0] — Vertical text scale (0.5–1.2)
   * @param {number} [params.gapMm=3] — Gap mm for resizing clearance
   * @param {boolean} [params.hasHallmark=true] — Whether hallmark is included (inside only)
   *
   * @returns {Object} { status: 'green'|'amber'|'red'|'empty', fillPct, message, fits }
   */
  function calculateFit(params) {
    var text = params.text || '';
    var fontName = params.font || 'Elvish';
    var ringSize = params.ringSize || 7;
    var bandWidth = params.bandWidth || 7.2;
    var position = params.position || 'outside';
    var vScale = params.vScale || 1.0;
    var gapMm = params.gapMm != null ? params.gapMm : DEFAULT_GAP_MM;
    var hasHallmark = params.hasHallmark != null ? params.hasHallmark : true;

    if (!text.trim()) {
      return { status: 'empty', fillPct: 0, message: '', fits: true };
    }

    // Find font config
    var fontCfg = FONT_CONFIGS.find(function (fc) {
      return fc.name === fontName || fc.key === fontName || fc.family === fontName;
    }) || FONT_CONFIGS[0];

    // Find band config
    var bandCfg = BAND_CONFIGS.find(function (b) { return b.nominal === bandWidth; }) || BAND_CONFIGS[3];

    // Get ring text (transliterate if Tengwar)
    var ringText = text;
    if (fontCfg.tengwar) {
      var t = transliterate(text);
      ringText = t.text;
    } else {
      ringText = text.replace(/\n/g, ' ').trim();
    }

    // Calculate circumference and usable space
    var circumMm = getCircumference(ringSize, bandWidth, position);
    var hallmarkMm = (position === 'inside' && hasHallmark) ? getHallmarkMm(bandWidth) : 0;
    var usableMm = circumMm - gapMm - hallmarkMm;

    // Measure text width
    var scalePxPerMm = 10; // reference scale for measurement
    var metrics = measureText(ringText, bandCfg.height, scalePxPerMm, vScale, fontCfg.family, fontCfg.style, fontCfg.scale, fontCfg.key);
    var textWidthMm = metrics.widthMm;

    // Calculate fill percentage
    var fillPct = usableMm > 0 ? (textWidthMm / usableMm) * 100 : 0;

    // Determine status
    var status, message;
    if (fillPct <= 85) {
      status = 'green';
      message = 'Fits comfortably';
    } else if (fillPct <= 100) {
      status = 'amber';
      message = 'Tight fit — text will be close to the edges';
    } else {
      status = 'red';
      message = 'Too long — try shortening or reducing text height';
    }

    return {
      status: status,
      fillPct: Math.round(fillPct),
      message: message,
      fits: fillPct <= 100,
      textWidthMm: Math.round(textWidthMm * 10) / 10,
      usableMm: Math.round(usableMm * 10) / 10,
      circumMm: Math.round(circumMm * 10) / 10,
    };
  }

  // ── Public API ─────────────────────────────────────────────────
  return {
    BAND_CONFIGS: BAND_CONFIGS,
    FONT_CONFIGS: FONT_CONFIGS,
    init: initMeasurement,
    calculateFit: calculateFit,
    measureText: measureText,
    transliterate: transliterate,
    getCircumference: getCircumference,
    getHallmarkMm: getHallmarkMm,
    isReady: function () { return _ready; },
  };
})();
